import { openContextModal } from '@mantine/modals';
import { debounce, diff, inRange, sleep } from 'radash';
import { proxy } from 'valtio';

import { publicConfig } from '@public-config';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';
import { type DiagramFootprint, DiagramHelpers } from '@diagram/helpers/DiagramHelpers';
import { ViewportService } from '@diagram/services/ViewportService';
import { WorstCaseService } from '@diagram/services/WorstCaseService';
import { canvasState } from '@diagram/state/canvas';
import { derivedDiagramState, diagram as diagramState } from '@diagram/state/diagram';
import { state as diagramModeState } from '@diagram/state/mode';

import type { Component, DiagramComponentInstance, DiagramPosition, Manufacturer } from 'models';

import { ApiService } from 'services/ApiService';
import { FileService } from 'services/FileService';
import { PayloadSDK } from 'services/PayloadSDK';

import {
    CELL_HEIGHT,
    CELL_WIDTH,
    DIAGRAM_EDGE_SIZE,
    DIAGRAM_DEFAULT_COLUMNS,
    DIAGRAM_DEFAULT_ROWS,
    DIAGRAM_MAX_COLUMNS,
    DIAGRAM_MAX_ROWS,
    DIAGRAM_MIN_COLUMNS,
    DIAGRAM_MIN_ROWS,
    printHeight,
    printWidth,
} from '../diagram-dimensions';
import { ComponentInstanceService } from './ComponentInstanceService';
import { ConnectionService } from './ConnectionService';
import { DiagramSyncService } from './DiagramSyncService';

const rangeState = proxy({
    from: {
        x: 0,
        y: 0,
    },
    to: {
        x: 0,
        y: 0,
    },
});

const MAX_NB_COMPONENTS_IN_VIEWPORT = 50;

const DiagramService = {
    get: () => DiagramSyncService.get(),

    getId: () => {
        const { id } = DiagramService.get();

        return id;
    },

    getDimensions: () => {
        const { columns, rows } = DiagramService.get();

        return {
            columns: columns ?? DIAGRAM_DEFAULT_COLUMNS,
            rows: rows ?? DIAGRAM_DEFAULT_ROWS,
        };
    },

    // The smallest size the diagram can shrink to without cutting off placed component instances
    getMinimumDimensions: () => {
        const grid = derivedDiagramState.grid;

        let columns = DIAGRAM_MIN_COLUMNS;
        let rows = DIAGRAM_MIN_ROWS;

        grid.forEach((row, y) => {
            row.forEach((cell, x) => {
                if (!cell) return;

                columns = Math.max(columns, x + 1);
                rows = Math.max(rows, y + 1);
            });
        });

        return { columns, rows };
    },

    getContentDimensions: () => {
        const { groups, textareas, infoBlocks, images } = DiagramService.get();

        let { columns, rows } = DiagramService.getMinimumDimensions();

        // Free-form elements are positioned in pixels, so convert their extents to cells
        const freeFormExtents = [
            ...groups.map(({ position, width, height }) => ({ position, width, height })),
            ...[...textareas, ...infoBlocks, ...images].map(({ position, dimensions }) => ({
                position,
                width: dimensions?.width ?? 0,
                height: dimensions?.height ?? 0,
            })),
        ];

        freeFormExtents.forEach(({ position, width, height }) => {
            columns = Math.max(columns, Math.ceil((position.x + width) / CELL_WIDTH));
            rows = Math.max(rows, Math.ceil((position.y + height) / CELL_HEIGHT));
        });

        return {
            columns: Math.min(columns, DIAGRAM_MAX_COLUMNS),
            rows: Math.min(rows, DIAGRAM_MAX_ROWS),
        };
    },

    // Resize the diagram to the smallest size that still fits all placed content
    fitToContent: () => {
        DiagramService.setDimensions(DiagramService.getContentDimensions());
        DiagramSyncService.save();
    },

    autoGrow: () => {
        const { columns, rows } = DiagramService.getDimensions();
        const componentInstances = Object.values(DiagramService.get().componentInstances);

        const growColumns =
            columns < DIAGRAM_MAX_COLUMNS &&
            componentInstances.some(({ position, colSpan }) => position.x + colSpan >= columns);

        const growRows =
            rows < DIAGRAM_MAX_ROWS && componentInstances.some(({ position, rowSpan }) => position.y + rowSpan >= rows);

        if (growColumns || growRows) {
            DiagramService.setDimensions({
                columns: growColumns ? columns + 1 : columns,
                rows: growRows ? rows + 1 : rows,
            });
        }
    },

    setDimensions: ({ columns, rows }: { columns?: number; rows?: number }) => {
        const minimum = DiagramService.getMinimumDimensions();

        if (columns !== undefined) {
            diagramState.columns = Math.min(Math.max(columns, minimum.columns), DIAGRAM_MAX_COLUMNS);
        }

        if (rows !== undefined) {
            diagramState.rows = Math.min(Math.max(rows, minimum.rows), DIAGRAM_MAX_ROWS);
        }

        // Re-clamp the viewport against the new canvas bounds
        ViewportService.setTransform(ViewportService.transform);
    },

    create: async (project: string, design: string, name: string) => {
        return PayloadSDK.create({
            collection: 'projectDesignDiagrams',
            data: {
                name,
                // @ts-ignore This will be used in the backend but won't be saved.
                design,
                project,
            },
        });
    },

    openForceRefreshModal: (type: 'loading' | 'saving') => {
        openContextModal({
            modal: 'forceRefresh',
            innerProps: {
                type,
            },
            closeOnEscape: false,
            closeOnClickOutside: false,
            withCloseButton: false,
        });
    },

    getNumberOfConnections: (componentInstance: DiagramComponentInstance): number => {
        const { connections } = DiagramService.get();

        return Object.values(connections).filter(
            (connection) =>
                connection.from.componentInstanceId === componentInstance.id ||
                connection.to.componentInstanceId === componentInstance.id,
        ).length;
    },

    getMaximumNumberOfConnections: (componentInstance: DiagramComponentInstance): number => {
        const { componentType, componentId, specifications } = componentInstance;

        const definition = ComponentInstanceHelpers.definition(componentType);

        if (componentId) {
            return specifications.electrical.ports.length;
        }

        return definition?.ports?.max ?? 1;
    },

    hasMaximumNumberOfConnections: (componentInstance: DiagramComponentInstance): boolean => {
        const numberOfConnections = DiagramService.getNumberOfConnections(componentInstance);
        const maximumNumberOfConnections = DiagramService.getMaximumNumberOfConnections(componentInstance);

        return numberOfConnections >= maximumNumberOfConnections;
    },

    getComponentInstanceAtPosition: (position: DiagramPosition): DiagramComponentInstance | null => {
        const componentInstanceId = derivedDiagramState.grid[position.y]?.[position.x];

        return componentInstanceId ? (DiagramService.get().componentInstances[componentInstanceId] ?? null) : null;
    },

    safeToPlace: (
        footprints: DiagramFootprint | DiagramFootprint[],
        excluded?: DiagramComponentInstance['id'] | DiagramComponentInstance['id'][],
    ): boolean => {
        if (!Array.isArray(footprints)) {
            return DiagramService.safeToPlace([footprints], excluded);
        }

        if (!Array.isArray(excluded)) {
            return DiagramService.safeToPlace(footprints, excluded ? [excluded] : []);
        }

        const grid = derivedDiagramState.grid;
        const excludedSet = new Set(excluded);
        const { columns, rows } = DiagramService.getDimensions();

        for (const { x, y, colSpan, rowSpan } of footprints) {
            // The full footprint must be within the diagram bounds, not just its origin cell
            if (x < 0 || y < 0 || x + colSpan > columns || y + rowSpan > rows) {
                return false;
            }

            for (let row = y; row < y + rowSpan; row++) {
                for (let column = x; column < x + colSpan; column++) {
                    const componentInstanceId = grid[row][column];

                    if (componentInstanceId && !excludedSet.has(componentInstanceId)) {
                        return false;
                    }
                }
            }
        }

        return true;
    },

    safeToMove: (componentInstances: DiagramComponentInstance[], difference: DiagramPosition): boolean => {
        return DiagramService.safeToPlace(
            componentInstances.map((componentInstance) => DiagramHelpers.footprint(componentInstance, difference)),
            componentInstances.map(({ id }) => id),
        );
    },

    downloadAsPng: async ({ filename = 'dcide.png' } = {}) => {
        const canvasElement = document.getElementById('diagram-canvas-content') as HTMLElement;

        if (!canvasElement) return false;

        canvasElement.classList.add('export-diagram');

        const originalTransform = ViewportService.transform;
        const reset = DiagramService.renderAllComponents();
        ViewportService.setTransform({ ...originalTransform, scale: 1 });

        try {
            // Make sure React re-rendered.
            await sleep(1000);

            // Export the entire diagram as sized by the user
            const { columns, rows } = DiagramService.getDimensions();

            const width = columns * CELL_WIDTH;
            const height = rows * CELL_HEIGHT;

            const { default: html2canvas } = await import('html2canvas');
            const canvas = await html2canvas(canvasElement, {
                x: 0,
                y: 0,
                width,
                height,
                useCORS: true,
                allowTaint: true,
                logging: false,
            });

            canvas.toBlob(
                (blob) => {
                    if (!blob) return;

                    FileService.download(blob, filename);
                },
                'image/png',
                1,
            );

            return true;
        } catch (error) {
            console.warn('Diagram PNG export failed', error);
            return false;
        } finally {
            reset();
            canvasElement.classList.remove('export-diagram');
            ViewportService.setTransform({ ...originalTransform });
        }
    },

    activatePrintMode: async () => {
        canvasState.printing = true;
        canvasState.printCoordinates = true;
        canvasState.printGrid = true;
        canvasState.printingViewportRecovery = ViewportService.transform;

        document.body.classList.add('diagram-printing');
        DiagramService.applyPrintLayout();
    },

    // Size the page to the diagram and put the diagram in its top-left corner
    applyPrintLayout: () => {
        const { columns, rows } = DiagramService.getDimensions();
        const coordinates = canvasState.printCoordinates;

        document.body.style.setProperty('--print-width', `${printWidth(columns, coordinates)}px`);
        document.body.style.setProperty('--print-height', `${printHeight(rows, coordinates)}px`);

        const offset = coordinates ? DIAGRAM_EDGE_SIZE : 0;
        ViewportService.alignToOrigin({ x: offset, y: offset });
    },

    setPrintCoordinates: (visible: boolean) => {
        canvasState.printCoordinates = visible;
        document.body.classList.toggle('diagram-hide-coordinates', !visible);

        DiagramService.applyPrintLayout();
    },

    setPrintGrid: (visible: boolean) => {
        canvasState.printGrid = visible;
        document.body.classList.toggle('diagram-hide-grid', !visible);
    },

    deactivatePrintMode: async () => {
        canvasState.printing = false;
        canvasState.printCoordinates = true;
        canvasState.printGrid = true;
        document.body.classList.remove('diagram-printing');
        document.body.classList.remove('diagram-monotone');
        document.body.classList.remove('diagram-hide-coordinates');
        document.body.classList.remove('diagram-hide-grid');
        document.body.style.removeProperty('--print-width');
        document.body.style.removeProperty('--print-height');

        if (canvasState.printingViewportRecovery) {
            ViewportService.setTransform(canvasState.printingViewportRecovery);
        }
    },

    createDiagramFromComponentTypes: (componentTypes: DiagramComponentInstance['componentType'][]) => {
        const localCoordinates = ViewportService.getCanvasCenter();
        const position = ViewportService.getCell(localCoordinates);
        ViewportService.moveIntoView(localCoordinates);

        const COMPONENT_TYPES_TO_POSITION_ON_TOP = ['solar', 'utility'];

        const componentTypesToPositionAbove = componentTypes.filter((componentType) =>
            COMPONENT_TYPES_TO_POSITION_ON_TOP.includes(componentType),
        );
        const componentTypesToPositionBelow = componentTypes.filter(
            (componentType) => !COMPONENT_TYPES_TO_POSITION_ON_TOP.includes(componentType),
        );

        const numberOfComponentInstances = Math.max(
            componentTypesToPositionAbove.length,
            componentTypesToPositionBelow.length,
        );

        // Place a panel of with numberOfComponentInstances components
        const panelId = ComponentInstanceService.add({
            componentType: 'panel',
            position: {
                x: position.x,
                y: position.y + 3,
            },
            colSpan: numberOfComponentInstances,
        });

        // Place components above panel
        const componentInstanceIdsAbove: DiagramComponentInstance['id'][] = [];
        componentTypesToPositionAbove.forEach((componentType, index) => {
            const componentInstanceId = ComponentInstanceService.add({
                componentType,
                position: { x: index + position.x, y: position.y },
            });
            componentInstanceIdsAbove.push(componentInstanceId);
        });

        // Place components below panel
        const componentInstanceIdsBelow: DiagramComponentInstance['id'][] = [];
        componentTypesToPositionBelow.forEach((componentType, index) => {
            const componentInstanceId = ComponentInstanceService.add({
                componentType,
                position: { x: index + position.x, y: position.y + 6 },
            });
            componentInstanceIdsBelow.push(componentInstanceId);
        });

        // Connect components above to the panel
        componentInstanceIdsAbove.forEach((componentInstanceId, index) => {
            ConnectionService.addConnectionWithEffects({
                from: {
                    componentInstanceId,
                    edge: 'bottom',
                    offset: 0,
                    port: null,
                },
                to: {
                    componentInstanceId: panelId,
                    edge: 'top',
                    offset: index,
                    port: null,
                },
            });
        });

        // Connect components below to the panel
        componentInstanceIdsBelow.forEach((componentInstanceId, index) => {
            ConnectionService.addConnectionWithEffects({
                from: {
                    componentInstanceId,
                    edge: 'top',
                    offset: 0,
                    port: null,
                },
                to: {
                    componentInstanceId: panelId,
                    edge: 'bottom',
                    offset: index,
                    port: null,
                },
            });
        });

        // Add converters in between components above
        for (let i = 0; i < componentInstanceIdsAbove.length; i++) {
            ComponentInstanceService.add({
                componentType: 'converter',
                position: { x: i + position.x, y: position.y + 1 },
            });
        }

        // Add converters in between components below
        for (let i = 0; i < componentInstanceIdsBelow.length; i++) {
            ComponentInstanceService.add({
                componentType: 'converter',
                position: { x: i + position.x, y: position.y + 5 },
            });
        }
    },

    createDiagramFromComponents: (components: Component[]) => {
        const localCoordinates = ViewportService.getCanvasCenter();
        const position = ViewportService.getCell(localCoordinates);
        ViewportService.moveIntoView(localCoordinates);

        components.forEach((component, index) => {
            ComponentInstanceService.add({
                componentType: component.type,
                position: { x: index + position.x, y: position.y },
                componentId: component.id,
                specifications: {
                    electrical: component.electrical as DiagramComponentInstance['specifications']['electrical'],
                },
            });
        });
    },

    isValidPosition: (position: DiagramPosition, colSpan = 1, rowSpan = 1) => {
        const { x, y } = position;
        const { columns, rows } = DiagramService.getDimensions();

        return x >= 0 && x + colSpan <= columns && y >= 0 && y + rowSpan <= rows;
    },

    setMode: (mode: typeof diagramModeState.mode) => {
        diagramModeState.mode = mode;
    },

    getManufacturers: async () => {
        const id = DiagramService.getId();

        return ApiService.get<Manufacturer[]>(`${publicConfig.urls.api}/projectDesignDiagrams/${id}/manufacturers`);
    },

    checkIfInViewport: (force?: boolean) => {
        const canvas = document.querySelector('[data-canvas-container]');

        if (!canvas || !canvasState.inViewportChecks) {
            return;
        }

        const numberOfComponentInstances = Object.keys(diagramState.componentInstances).length;

        // If there are less than MAX_NB_COMPONENTS_IN_VIEWPORT, all component instances are in the viewport
        if (numberOfComponentInstances < MAX_NB_COMPONENTS_IN_VIEWPORT) {
            canvasState.componentInstancesInViewport = Object.keys(diagramState.componentInstances);

            return;
        }

        const bcr = canvas.getBoundingClientRect();

        const from = ViewportService.getCell(ViewportService.getLocalCoordinates({ x: bcr.x, y: bcr.y }));
        const to = ViewportService.getCell(
            ViewportService.getLocalCoordinates({
                x: bcr.x + bcr.width,
                y: bcr.y + bcr.height,
            }),
        );

        const localRange = {
            from,
            to,
        };

        if (force || JSON.stringify(localRange) !== JSON.stringify(rangeState)) {
            rangeState.from = localRange.from;
            rangeState.to = localRange.to;

            const next: string[] = [];

            Object.values(diagramState.componentInstances).forEach((componentInstance) => {
                const x = {
                    min: localRange.from.x - 1,
                    max: localRange.to.x + 1,
                };

                const y = {
                    min: localRange.from.y - 1,
                    max: localRange.to.y + 1,
                };

                const points = {
                    x: [] as number[],
                    y: [] as number[],
                };

                for (
                    let x = componentInstance.position.x;
                    x < componentInstance.position.x + componentInstance.colSpan;
                    x++
                ) {
                    points.x.push(x);
                }

                for (
                    let y = componentInstance.position.y;
                    y < componentInstance.position.y + componentInstance.rowSpan;
                    y++
                ) {
                    points.y.push(y);
                }

                const intersects = {
                    x: points.x.some((point) => inRange(point, x.min, x.max)),
                    y: points.y.some((point) => inRange(point, y.min, y.max)),
                };

                if (intersects.x && intersects.y) {
                    next.push(componentInstance.id);
                }
            });

            const ids = canvasState.componentInstancesInViewport;

            if (diff(ids, next).length || diff(next, ids).length) {
                canvasState.componentInstancesInViewport = next;
            }
        }
    },

    debouncedCheckIfInViewport: debounce(
        {
            delay: 200,
        },
        () => {
            DiagramService.checkIfInViewport();
        },
    ),

    renderAllComponents: () => {
        canvasState.inViewportChecks = false;
        canvasState.componentInstancesInViewport = Object.keys(diagramState.componentInstances);

        return () => {
            canvasState.inViewportChecks = true;
        };
    },
};

export { DiagramService };
